import { AddOnPermissionType, AddOnStatus, AddOnType, Asset, BillingCycle, Currency, Env, HighlightFeature, Markdown, Price, SettingsConfig, UsageStats, providers, type Provider } from "../../../index";

type AddOnPlanProps = {
  billingCycle: BillingCycle;
  internalId: string;
  externalId: string;
  prices: Price[];
};

type JsonObject = Record<string, unknown>;

type AddOnPlanJson = {
  billing_cycle: JsonObject;
  internal_id: string;
  external_id: string;
  prices: JsonObject[];
};

type AddOnJson = {
  internal_id: string;
  external_id: string;
  short_name: string;
  long_name: string;
  short_description: Markdown;
  long_description: Markdown;
  type: AddOnType;
  icon_asset: JsonObject;
  cover_asset: JsonObject;
  preview_assets?: JsonObject[];
  docs_url: string;
  providers_ids: string[];
  status: AddOnStatus;
  plans?: AddOnPlanJson[];
  settings_config?: JsonObject[];
  usage_stats?: JsonObject[];
  highlight_features?: JsonObject[];
  allowed_permission_types?: AddOnPermissionType[];
};

class AddOnPlan {
  constructor(public _: AddOnPlanProps) {}

  toJson = () => {
    return {
      billing_cycle: this._.billingCycle.toJson(),
      internal_id: this._.internalId,
      external_id: this._.externalId,
      prices: this._.prices.map((price) => price.toJson()),
    };
  };

  static fromJson = (json: AddOnPlanJson) => {
    return new AddOnPlan({
      billingCycle: BillingCycle.fromJson(json.billing_cycle),
      internalId: json.internal_id,
      externalId: json.external_id,
      prices: json.prices.map((price) => Price.fromJson(price)),
    });
  };

  priceByCurrency = (currency: Currency) => {
    return this._.prices.find((price) => price.currency === currency);
  };
}

interface AddOnConstructorProps {
  internalId: string;
  externalId: string;
  shortName: string;
  longName: string;
  shortDescription: Markdown;
  longDescription: Markdown;
  type: AddOnType;
  iconAsset: Asset;
  coverAsset: Asset;
  previewAssets: Asset[];
  docsUrl: string;
  providersIds: string[];
  status: AddOnStatus;
  plans: AddOnPlan[];
  settingsConfig: SettingsConfig[];
  usageStats?: UsageStats[];
  highlightFeatures?: HighlightFeature[];
  allowedPermissionTypes?: AddOnPermissionType[];
}

class AddOn {
  constructor(public _: AddOnConstructorProps) {}

  planFromInternalId = (id: string) => {
    return this._.plans.find((plan) => plan._.internalId === id);
  };

  planFromExternalId = (id: string) => {
    return this._.plans.find((plan) => plan._.externalId === id);
  };

  planByBillingCycle = (billingCycle: BillingCycle) => {
    return this._.plans.find((plan) =>
      plan._.billingCycle?.equals(billingCycle)
    );
  };

	providers = (env: Env) => {
		return providers(env).providers.filter((provider: Provider) =>
			this._.providersIds.includes(provider._.id)
		);
	};

  toJson = () => {
    return {
      internal_id: this._.internalId,
      external_id: this._.externalId,
      short_name: this._.shortName,
      long_name: this._.longName,
      short_description: this._.shortDescription,
      long_description: this._.longDescription,
      type: this._.type,
      icon_asset: this._.iconAsset.toJson(),
      cover_asset: this._.coverAsset.toJson(),
      preview_assets: this._.previewAssets.map((asset) => asset.toJson()),
      docs_url: this._.docsUrl,
      providersIds: this._.providersIds,
      status: this._.status,
      plans: this._.plans.map((plan) => plan.toJson()),
      settings_config: this._.settingsConfig.map((config) => config.toJson()),
      usage_stats: this._.usageStats?.map((usageStat) => usageStat.toJson()),
      highlight_features: this._.highlightFeatures?.map((feature) =>
        feature.toJson()
      ),
      allowed_permission_types: this._.allowedPermissionTypes,
    };
  };

  static fromJson = (json: AddOnJson) => {
    return new AddOn({
      internalId: json.internal_id,
      externalId: json.external_id,
      shortName: json.short_name,
      longName: json.long_name,
      shortDescription: json.short_description,
      longDescription: json.long_description,
      type: json.type,
      iconAsset: Asset.fromJson(json.icon_asset),
      coverAsset: Asset.fromJson(json.cover_asset),
      previewAssets:
        json.preview_assets?.map((asset) => Asset.fromJson(asset)) || [],
      docsUrl: json.docs_url,
      providersIds: json.providers_ids,
      status: json.status,
      plans: json.plans?.map((plan) => AddOnPlan.fromJson(plan)) || [],
      settingsConfig:
        json.settings_config?.map((config) => SettingsConfig.fromJson(config)) ||
        [],
      usageStats:
        json.usage_stats?.map((usageStat) => UsageStats.fromJson(usageStat)) ||
        [],
      highlightFeatures:
        json.highlight_features?.map((feature) =>
          HighlightFeature.fromJson(feature)
        ) || [],
      allowedPermissionTypes: json.allowed_permission_types,
    });
  };
}

interface AddOnsCatalogProps {
  addOns: AddOn[];
}

class AddOnsCatalog {
  constructor(private _: AddOnsCatalogProps) {}

  get addOns() {
    return this._.addOns;
  }

  addOnFromInternalId = (id: string) => {
    return this._.addOns.find((addOn) => addOn._.internalId === id);
  };

  addOnFromExternalId = (id: string) => {
    return this._.addOns.find((addOn) => addOn._.externalId === id);
  };

  addOnFromExternalIdInPlan = (id: string) => {
    return this._.addOns.find((addOn) =>
      addOn._.plans.some((plan) => plan._.externalId === id)
    );
  };

  addOnFromInternalIdInPlan = (id: string) => {
    return this._.addOns.find((addOn) =>
      addOn._.plans.some((plan) => plan._.internalId === id)
    );
  };

  planFromInternalId = (id: string) => {
    const addOn = this._.addOns.find((addOn) =>
      addOn._.plans.some((plan) => plan._.internalId === id)
    );
    return addOn?._.plans.find((plan) => plan._.internalId === id);
  };

  planFromExternalId = (id: string) => {
    const addOn = this._.addOns.find((addOn) =>
      addOn._.plans.some((plan) => plan._.externalId === id)
    );
    return addOn?._.plans.find((plan) => plan._.externalId === id);
  };
}

export { AddOn, AddOnsCatalog, AddOnPlan };
